import { useCallback, useEffect } from 'react';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { useUpdateExternalUser, UserLoginStatus } from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { useBaseDialogForm } from '@spendbrain/common';
import { z } from 'zod';

export enum PhoneNumberFormField {
  PhoneNumber = 'phoneNumber',
  DontAskAgain = 'dontAskAgain',
}

export const useValidationSchema = () => {
  const { t } = useTranslation();

  return z.object({
    [PhoneNumberFormField.PhoneNumber]: z
      .string()
      .optional()
      .refine(
        val => {
          if (!val || val.trim() === '') return true;

          return isPossiblePhoneNumber(val);
        },
        {
          message: t('validation.invalidPhoneNumber'),
        }
      ),
    [PhoneNumberFormField.DontAskAgain]: z.boolean(),
  });
};

type PhoneNumberFormValues = z.infer<ReturnType<typeof useValidationSchema>>;

const defaultValues: PhoneNumberFormValues = {
  [PhoneNumberFormField.PhoneNumber]: '',
  [PhoneNumberFormField.DontAskAgain]: false,
};

interface UsePhoneNumberFormProps {
  userId: string;
  onSuccess: (statusUpdated: boolean) => void;
}

export const usePhoneNumberForm = ({
  userId,
  onSuccess,
}: UsePhoneNumberFormProps) => {
  const { open } = useNotification();
  const { t } = useTranslation();
  const validationSchema = useValidationSchema();

  const { mutateAsync: updateUser, isPending } = useUpdateExternalUser({
    onSuccess: () => {
      open?.({
        type: 'success',
        message: t('phoneNumberModal.successMessage.message'),
        description: t('phoneNumberModal.successMessage.description'),
      });
      onSuccess(true);
    },
    onError: error => {
      console.error(error);
      open?.({
        type: 'error',
        message: t('phoneNumberModal.errorMessage.message'),
        description: t('phoneNumberModal.errorMessage.description'),
      });
    },
  });

  const onFormSubmit = useCallback(
    async (values: PhoneNumberFormValues) => {
      try {
        const hasPhoneNumber = values[PhoneNumberFormField.PhoneNumber]?.trim();
        const dontAskAgain = values[PhoneNumberFormField.DontAskAgain];

        if (!hasPhoneNumber && !dontAskAgain) {
          onSuccess(false);

          return;
        }

        const data: {
          userLoginStatus: UserLoginStatus;
          contactPhoneNumber?: string;
        } = {
          userLoginStatus: UserLoginStatus.Completed,
        };

        if (hasPhoneNumber && !dontAskAgain)
          data.contactPhoneNumber = hasPhoneNumber;

        await updateUser({ userId, data });
      } catch (e) {
        return e;
      }
    },
    [updateUser, userId, onSuccess]
  );

  const { formProps, onSubmit } = useBaseDialogForm({
    onSubmit: onFormSubmit,
    defaultValues,
    validationSchema,
    mode: 'onSubmit',
  });

  const dontAskAgain = formProps.watch(PhoneNumberFormField.DontAskAgain);

  useEffect(() => {
    if (dontAskAgain)
      formProps.setValue(PhoneNumberFormField.PhoneNumber, '', {
        shouldValidate: true,
      });
  }, [dontAskAgain, formProps]);

  return {
    formProps,
    onSubmit,
    isPending,
  };
};
